import React, { useState, useRef } from 'react';
import { schoolListRequestsAPI } from '../../utils/api';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const GRADES = ['Pre-K','K','1','2','3','4','5','6','7','8','9','10','11','12'];

// --- Sub-components ---

const InputField = ({ label, required, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#4b5563', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '0.6rem 0.85rem', border: '1.5px solid #e5e7eb', borderRadius: 8,
  fontSize: '0.9rem', color: '#1f2937', background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const FileUploadZone = ({ file, onFile }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div
      onClick={() => inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? '#6366f1' : file ? '#10b981' : '#c7d2fe'}`,
        borderRadius: 12, padding: '1.5rem 1rem', textAlign: 'center', cursor: 'pointer',
        background: dragging ? '#eef2ff' : file ? '#f0fdf4' : '#f8faff',
        transition: 'all 0.2s',
      }}
    >
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
      {file ? (
        <div>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📎</div>
          <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>{file.name}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
            {(file.size / 1024).toFixed(0)} KB · Click to change
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>☁️</div>
          <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '0.9rem' }}>Click to upload</div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 3 }}>or drag & drop · PDF, Word, image, text</div>
        </div>
      )}
    </div>
  );
};

// --- Main Component ---

const SchoolNotFound = ({ searchTerm = '' }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = school info, 2 = supply list, 3 = success
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    school_name: searchTerm || '',
    school_city: '', school_state: '', grade: '',
    parent_name: '', email: '', phone: '', list_text: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleFile = async (f) => {
    if (f.size > 5 * 1024 * 1024) { setError('File must be under 5 MB'); return; }
    setFile(f);
    setError('');
  };

  const goStep2 = () => {
    if (!form.school_name.trim() || !form.parent_name.trim() || !form.email.trim()) {
      setError('School name, your name and email are required'); return;
    }
    setError(''); setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      let file_name = null, file_data = null;
      if (file) {
        file_name = file.name;
        file_data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      await schoolListRequestsAPI.create({ ...form, file_name, file_data });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOpen(false); setStep(1); setFile(null); setError('');
    setForm({ school_name: searchTerm || '', school_city: '', school_state: '', grade: '', parent_name: '', email: '', phone: '', list_text: '' });
  };

  return (
    <>
      {/* Trigger banner */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 14, padding: '1.25rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
        boxShadow: '0 4px 20px rgba(99,102,241,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
          <div style={{ fontSize: '2rem' }}>🏫</div>
          <div>
            <div style={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>Can't find your school?</div>
            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem', marginTop: 2 }}>
              Send us your supply list — we'll build a custom kit and get back within 24–48 hours.
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          style={{
            background: '#fff', color: '#4f46e5', fontWeight: 700, fontSize: '0.88rem',
            border: 'none', borderRadius: 8, padding: '0.55rem 1.2rem', cursor: 'pointer',
            whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', transition: 'opacity 0.15s',
          }}
        >
          Upload My List →
        </button>
      </div>

      {/* Modal */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }} onClick={e => e.target === e.currentTarget && reset()}>
          <div style={{
            background: '#fff', borderRadius: 18, width: '100%', maxWidth: 530,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          }}>
            {/* Modal header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '18px 18px 0 0', padding: '1.5rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            }}>
              <div>
                <div style={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem' }}>
                  {step === 3 ? '🎉 Request Submitted!' : "Share Your Supply List"}
                </div>
                {step !== 3 && (
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', marginTop: 3 }}>
                    Step {step} of 2 · We'll respond within 24–48 hours
                  </div>
                )}
              </div>
              <button onClick={reset} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: '#fff', width: 30, height: 30, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            <div style={{ padding: '1.5rem' }}>

              {/* Step indicator */}
              {step !== 3 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {[1, 2].map(s => (
                    <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? '#6366f1' : '#e5e7eb', transition: 'background 0.3s' }} />
                  ))}
                </div>
              )}

              {/* Success state */}
              {step === 3 && (
                <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                  <h3 style={{ fontWeight: 800, color: '#1f2937', marginBottom: '0.5rem' }}>You're all set!</h3>
                  <p style={{ color: '#4b5563', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                    We've received your supply list for <strong>{form.school_name}</strong>.
                    Our team will review it and email <strong>{form.email}</strong> within 24–48 hours with a custom kit.
                  </p>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '0.9rem 1rem', color: '#166534', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                    📧 Confirmation sent to {form.email}
                  </div>
                  <button onClick={reset} className="btn btn-primary" style={{ width: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
                    Close
                  </button>
                </div>
              )}

              {/* Step 1: School + Contact */}
              {step === 1 && (
                <>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem', fontSize: '0.95rem' }}>🏫 School Details</div>
                    <InputField label="School Name" required>
                      <input style={inputStyle} value={form.school_name} onChange={e => set('school_name', e.target.value)} placeholder="Lincoln Elementary School" />
                    </InputField>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem' }}>
                      <InputField label="City">
                        <input style={inputStyle} value={form.school_city} onChange={e => set('school_city', e.target.value)} placeholder="San Francisco" />
                      </InputField>
                      <InputField label="State">
                        <select style={{ ...inputStyle, width: 80 }} value={form.school_state} onChange={e => set('school_state', e.target.value)}>
                          <option value="">--</option>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </InputField>
                    </div>
                    <InputField label="Grade">
                      <select style={inputStyle} value={form.grade} onChange={e => set('grade', e.target.value)}>
                        <option value="">Select grade</option>
                        {GRADES.map(g => <option key={g} value={g}>{g === 'Pre-K' ? 'Pre-K' : `Grade ${g}`}</option>)}
                      </select>
                    </InputField>
                  </div>

                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem', fontSize: '0.95rem' }}>👤 Your Details</div>
                    <InputField label="Your Name" required>
                      <input style={inputStyle} value={form.parent_name} onChange={e => set('parent_name', e.target.value)} placeholder="Jane Smith" />
                    </InputField>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <InputField label="Email" required>
                        <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" />
                      </InputField>
                      <InputField label="Phone">
                        <input style={inputStyle} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(415) 555-0100" />
                      </InputField>
                    </div>
                  </div>

                  {error && <div style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', borderRadius: 6 }}>{error}</div>}
                  <button onClick={goStep2} className="btn btn-primary" style={{ width: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', padding: '0.75rem' }}>
                    Next: Upload Supply List →
                  </button>
                </>
              )}

              {/* Step 2: Supply list */}
              {step === 2 && (
                <>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.75rem', fontSize: '0.95rem' }}>📎 Upload your supply list</div>
                    <FileUploadZone file={file} onFile={handleFile} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0' }}>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600 }}>OR</span>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                  </div>

                  <InputField label="Paste / type your list">
                    <textarea
                      style={{ ...inputStyle, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
                      value={form.list_text}
                      onChange={e => set('list_text', e.target.value)}
                      placeholder={'Example:\n- 2x Wide-ruled composition notebook\n- 12-count Crayola markers\n- 1x Pencil pouch'}
                    />
                  </InputField>

                  <div style={{ background: '#eff6ff', borderRadius: 10, padding: '0.75rem 1rem', color: '#1d4ed8', fontSize: '0.8rem', marginBottom: '1rem', lineHeight: 1.6 }}>
                    💡 <strong>Tip:</strong> The more details you add (brand, size, quantity), the faster we can build your kit!
                  </div>

                  {error && <div style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', borderRadius: 6 }}>{error}</div>}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <button onClick={() => { setStep(1); setError(''); }} className="btn btn-outline" style={{ padding: '0.75rem' }}>← Back</button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading || (!file && !form.list_text.trim())}
                      className="btn btn-primary"
                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', padding: '0.75rem', opacity: (!file && !form.list_text.trim()) ? 0.6 : 1 }}
                    >
                      {loading ? 'Submitting…' : 'Submit Request 🚀'}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SchoolNotFound;
